import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navigation from '../home/components/Navigation';

const InsightsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'AI Technology', 'Creative Process', 'Industry Trends', 'Case Studies'];

  const articles = [
    {
      id: 1,
      title: 'The Future of AI in Creative Industries',
      excerpt: 'Exploring how artificial intelligence is revolutionizing the way we create and consume visual content in 2024 and beyond.',
      category: 'AI Technology',
      date: 'March 15, 2024',
      readTime: '8 min read',
      image: 'https://public.readdy.ai/ai/img_res/bc8c511f519fe49e2a3661d67220ae20.jpg',
      author: 'Sarah Chen',
      featured: true
    },
    {
      id: 2,
      title: 'Behind the Scenes: Creating Cinematic AI Videos',
      excerpt: 'A deep dive into our process of combining AI technology with traditional cinematography techniques.',
      category: 'Creative Process',
      date: 'March 12, 2024',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=Behind%20the%20scenes%20video%20production%20with%20AI%20technology%2C%20professional%20camera%20setup%2C%20hot%20pink%20and%20aqua%20lighting%2C%20cinematic%20atmosphere%2C%20studio%20photography%2C%208k%20detail&width=800&height=600&seq=blog-2&orientation=landscape',
      author: 'Marcus Rodriguez'
    },
    {
      id: 3,
      title: 'Why Hybrid Content is Taking Over Marketing',
      excerpt: 'Discover why brands are increasingly turning to hybrid AI-generated content for their campaigns.',
      category: 'Industry Trends',
      date: 'March 10, 2024',
      readTime: '5 min read',
      image: 'https://readdy.ai/api/search-image?query=Modern%20marketing%20campaign%20with%20hybrid%20digital%20content%2C%20hot%20pink%20and%20aqua%20color%20scheme%2C%20professional%20business%20setting%2C%20innovative%20technology%2C%20high%20quality%20photography&width=800&height=600&seq=blog-3&orientation=landscape',
      author: 'Emily Watson'
    },
    {
      id: 4,
      title: 'Case Study: Transforming Brand Identity with AI',
      excerpt: 'How we helped a Fortune 500 company reimagine their visual identity using AI-generated imagery.',
      category: 'Case Studies',
      date: 'March 8, 2024',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=Professional%20brand%20identity%20design%20with%20AI%20elements%2C%20corporate%20setting%2C%20golden%20amber%20accents%2C%20modern%20office%20environment%2C%20high-end%20photography%2C%208k%20quality&width=800&height=600&seq=blog-4&orientation=landscape',
      author: 'David Kim'
    },
    {
      id: 5,
      title: 'The Art of Prompt Engineering for Stunning Visuals',
      excerpt: 'Master the techniques of crafting perfect prompts to generate exactly what you envision.',
      category: 'Creative Process',
      date: 'March 5, 2024',
      readTime: '7 min read',
      image: 'https://readdy.ai/api/search-image?query=Creative%20workspace%20with%20AI%20prompt%20engineering%2C%20digital%20screens%20showing%20text%20and%20images%2C%20teal%20lighting%2C%20professional%20studio%20setup%2C%20innovative%20technology%2C%208k%20detail&width=800&height=600&seq=blog-5&orientation=landscape',
      author: 'Sarah Chen'
    },
    {
      id: 6,
      title: 'AI Ethics in Creative Content Generation',
      excerpt: 'Navigating the ethical considerations and best practices when using AI for content creation.',
      category: 'Industry Trends',
      date: 'March 3, 2024',
      readTime: '9 min read',
      image: 'https://readdy.ai/api/search-image?query=Ethics%20and%20technology%20concept%2C%20balanced%20scales%20with%20AI%20elements%2C%20professional%20photography%2C%20golden%20teal%20color%20palette%2C%20thoughtful%20composition%2C%208k%20quality&width=800&height=600&seq=blog-6&orientation=landscape',
      author: 'Marcus Rodriguez'
    }
  ];

  const filteredArticles = selectedCategory === 'All' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  const featuredArticle = articles.find(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-pink/5 to-transparent" />
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-7xl font-light mb-6">
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Insights</span>
            </h1>
            <p className="text-2xl text-white/60 max-w-3xl mx-auto">
              Thoughts, stories, and ideas from the intersection of AI and creativity
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {categories.map((category) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-brand-pink to-brand-aqua text-white'
                    : 'bg-white/5 text-white/60 hover:text-white border border-white/10'
                }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && selectedCategory === 'All' && (
        <section className="py-24">
          <div className="max-w-[1400px] mx-auto px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-zinc-900 to-black border border-white/10 group cursor-pointer"
            >
              <div className="grid grid-cols-2 gap-0">
                <div className="relative h-[600px] overflow-hidden">
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-6 left-6">
                    <span className="px-4 py-2 rounded-full bg-brand-pink text-white text-sm font-bold">
                      Featured
                    </span>
                  </div>
                </div>

                <div className="flex flex-col justify-center p-16">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="px-4 py-2 rounded-full bg-brand-aqua/10 border border-brand-aqua/30 text-brand-aqua text-sm font-semibold">
                      {featuredArticle.category}
                    </span>
                    <span className="text-white/40 text-sm">{featuredArticle.date}</span>
                    <span className="text-white/40 text-sm">•</span>
                    <span className="text-white/40 text-sm">{featuredArticle.readTime}</span>
                  </div>

                  <h2 className="text-5xl font-light text-white mb-6 leading-tight">
                    {featuredArticle.title}
                  </h2>
                  
                  <p className="text-lg text-white/70 mb-8 leading-relaxed">
                    {featuredArticle.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-pink to-brand-aqua flex items-center justify-center">
                        <span className="text-white font-bold">{featuredArticle.author[0]}</span>
                      </div>
                      <span className="text-white/80">{featuredArticle.author}</span>
                    </div>

                    <motion.div
                      whileHover={{ x: 10 }}
                      className="flex items-center gap-3 text-brand-aqua font-semibold"
                    >
                      <span>Read Article</span>
                      <i className="ri-arrow-right-line text-xl"></i>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-3 gap-8">
            {regularArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group cursor-pointer"
              >
                <div className="relative h-64 rounded-2xl overflow-hidden mb-6">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-brand-pink text-xs font-semibold">
                    {article.category}
                  </span>
                  <span className="text-white/40 text-xs">{article.date}</span>
                  <span className="text-white/40 text-xs">•</span>
                  <span className="text-white/40 text-xs">{article.readTime}</span>
                </div>

                <h3 className="text-2xl font-semibold text-white mb-3 leading-tight group-hover:text-brand-pink transition-colors">
                  {article.title}
                </h3>

                <p className="text-white/60 mb-4 leading-relaxed">
                  {article.excerpt}
                </p>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-pink to-brand-aqua flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{article.author[0]}</span>
                  </div>
                  <span className="text-white/60 text-sm">{article.author}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-pink/10 to-brand-aqua/10 border border-brand-pink/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Get the latest insights on AI and creativity delivered to your inbox
            </p>
            <div className="flex gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-aqua/50 transition-colors"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold whitespace-nowrap cursor-pointer"
              >
                Subscribe
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;